import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { X, Upload, Trash2, Plus, Minus } from "lucide-react";
import axios from "axios";
import { constants } from "../../../constants";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Editor.css";

const AddEventForm = ({ addEvent, cancel }) => {
  const [title, setTitle] = useState("");
  const [presentedBy, setPresentedBy] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [topics, setTopics] = useState([""]);
  const [locations, setLocations] = useState([{ city: "", date: "", venue: "" }]);
  const [presenterName, setPresenterName] = useState("");
  const [presenterTitle, setPresenterTitle] = useState("");
  const [relatedEvents, setRelatedEvents] = useState([{ title: "", date: "" }]);
  const [image, setImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [presenterImage, setPresenterImage] = useState(null);
  const [previewPresenterImage, setPreviewPresenterImage] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const presenterFileInputRef = useRef(null);

  const modules = {
    toolbar: [
      [{ font: [] }, { size: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handlePresenterImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPresenterImage(file);
      setPreviewPresenterImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemovePresenterImage = () => {
    setPresenterImage(null);
    setPreviewPresenterImage(null);
    if (presenterFileInputRef.current) {
      presenterFileInputRef.current.value = "";
    }
  };

  const addTopic = () => {
    setTopics([...topics, ""]);
  };

  const removeTopic = (index) => {
    setTopics(topics.filter((_, i) => i !== index));
  };

  const updateTopic = (index, value) => {
    const newTopics = [...topics];
    newTopics[index] = value;
    setTopics(newTopics);
  };

  const addLocation = () => {
    setLocations([...locations, { city: "", date: "", venue: "" }]);
  };

  const removeLocation = (index) => {
    setLocations(locations.filter((_, i) => i !== index));
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...locations];
    newLocations[index][field] = value;
    setLocations(newLocations);
  };

  const addRelatedEvent = () => {
    setRelatedEvents([...relatedEvents, { title: "", date: "" }]);
  };

  const removeRelatedEvent = (index) => {
    setRelatedEvents(relatedEvents.filter((_, i) => i !== index));
  };

  const updateRelatedEvent = (index, field, value) => {
    const newRelatedEvents = [...relatedEvents];
    newRelatedEvents[index][field] = value;
    setRelatedEvents(newRelatedEvents);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!image) {
      setError("Please upload a cover image");
      return;
    }

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append("title", title);
    formData.append("presentedBy", presentedBy);
    formData.append("date", date);
    formData.append("time", time);
    formData.append("location", location);
    formData.append("description", description);
    formData.append("image", image);
    
    // Filter out empty topics
    const validTopics = topics.filter(topic => topic.trim() !== "");
    formData.append("topics", JSON.stringify(validTopics));
    
    // Filter out incomplete locations
    const validLocations = locations.filter(loc => loc.city && loc.date && loc.venue);
    formData.append("locations", JSON.stringify(validLocations));
    
    // Presenter info
    if (presenterName) formData.append("presenterName", presenterName);
    if (presenterTitle) formData.append("presenterTitle", presenterTitle);
    if (presenterImage) formData.append("presenterImage", presenterImage);
    
    // Filter out incomplete related events
    const validRelatedEvents = relatedEvents.filter(event => event.title && event.date);
    formData.append("relatedEvents", JSON.stringify(validRelatedEvents));

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.post(
        `${constants.serverURL}/api/events/addEvent`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Call the addEvent function passed from parent
      cancel();
      addEvent(response.data);
    }
    catch (err) {
     
      console.error("Add event error:", err);
    }
  };

  return (
    <div className="p-6 bg-gray-50 pb-">
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="bg-white rounded-lg shadow-md mb-[15rem]"
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Create New Event
          </h1>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={cancel}
            className="text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </motion.button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Main Event Image Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Upload Event Cover Image
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md cursor-pointer hover:bg-blue-600 transition"
              >
                <Upload size={20} className="mr-2" />
                Choose Image
              </label>
              {previewImage && (
                <div className="relative">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Basic Event Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-gray-700 text-sm font-bold mb-2">
                Event Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter event title"
              />
            </div>
            <div>
              <label htmlFor="presentedBy" className="block text-gray-700 text-sm font-bold mb-2">
                Presented By
              </label>
              <input
                type="text"
                id="presentedBy"
                value={presentedBy}
                onChange={(e) => setPresentedBy(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Organization or presenter name"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="date" className="block text-gray-700 text-sm font-bold mb-2">
                Date Range
              </label>
              <input
                type="text"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. April 10-13, 2025"
              />
            </div>
            <div>
              <label htmlFor="time" className="block text-gray-700 text-sm font-bold mb-2">
                Time
              </label>
              <input
                type="text"
                id="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 10:00 AM – 6:00 PM"
              />
            </div>
            <div>
              <label htmlFor="location" className="block text-gray-700 text-sm font-bold mb-2">
                General Location
              </label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Multiple Cities"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-gray-700 text-sm font-bold mb-2">
              Event Description
            </label>
            <ReactQuill
              theme="snow"
              value={description}
              onChange={setDescription}
              modules={modules}
              placeholder="Write event description..."
            />
          </div>

          {/* Topics/Features Section */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Event Topics/Features
            </label>
            {topics.map((topic, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => updateTopic(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter topic or feature"
                />
                {topics.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTopic(index)}
                    className="ml-2 p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Minus size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addTopic}
              className="flex items-center text-blue-500 hover:text-blue-600"
            >
              <Plus size={16} className="mr-1" />
              Add Topic
            </button>
          </div>

          {/* Event Locations */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Event Locations
            </label>
            {locations.map((loc, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 mb-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-600 text-sm mb-1">City</label>
                    <input
                      type="text"
                      value={loc.city}
                      onChange={(e) => updateLocation(index, "city", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm mb-1">Date</label>
                    <input
                      type="date"
                      value={loc.date}
                      onChange={(e) => updateLocation(index, "date", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm mb-1">Venue</label>
                    <select
                      value={loc.venue}
                      onChange={(e) => updateLocation(index, "venue", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select venue</option>
                      <option value="Ahmedabad">Ahmedabad</option>
                      <option value="Anand">Anand</option>
                      <option value="Navsari">Navsari</option>
                      <option value="Surat">Surat</option>
                    </select>
                  </div>
                </div>
                {locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLocation(index)}
                    className="mt-2 flex items-center text-red-500 hover:text-red-600"
                  >
                    <Minus size={16} className="mr-1" />
                    Remove Location
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addLocation}
              className="flex items-center text-blue-500 hover:text-blue-600"
            >
              <Plus size={16} className="mr-1" />
              Add Location
            </button>
          </div>

          {/* Presenter Information */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Presenter Information</h3>
            
            {/* Presenter Image Upload */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Presenter Image (Optional)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  ref={presenterFileInputRef}
                  onChange={handlePresenterImageChange}
                  accept="image/*"
                  className="hidden"
                  id="presenter-image-upload"
                />
                <label
                  htmlFor="presenter-image-upload"
                  className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md cursor-pointer hover:bg-green-600 transition"
                >
                  <Upload size={20} className="mr-2" />
                  Choose Presenter Image
                </label>
                {previewPresenterImage && (
                  <div className="relative">
                    <img
                      src={previewPresenterImage}
                      alt="Presenter Preview"
                      className="w-20 h-20 object-cover rounded-full"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePresenterImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="presenterName" className="block text-gray-700 text-sm font-bold mb-2">
                  Presenter Name
                </label>
                <input
                  type="text"
                  id="presenterName"
                  value={presenterName}
                  onChange={(e) => setPresenterName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Presenter or organization name"
                />
              </div>
              <div>
                <label htmlFor="presenterTitle" className="block text-gray-700 text-sm font-bold mb-2">
                  Presenter Title/Role
                </label>
                <input
                  type="text"
                  id="presenterTitle"
                  value={presenterTitle}
                  onChange={(e) => setPresenterTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Title or role description"
                />
              </div>
            </div>
          </div>

          {/* Related Events */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Related Events (Optional)
            </label>
            {relatedEvents.map((event, index) => (
              <div key={index} className="flex items-center space-x-4 mb-2">
                <input
                  type="text"
                  value={event.title}
                  onChange={(e) => updateRelatedEvent(index, "title", e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Related event title"
                />
                <input
                  type="text"
                  value={event.date}
                  onChange={(e) => updateRelatedEvent(index, "date", e.target.value)}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Date"
                />
                {relatedEvents.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRelatedEvent(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Minus size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addRelatedEvent}
              className="flex items-center text-blue-500 hover:text-blue-600"
            >
              <Plus size={16} className="mr-1" />
              Add Related Event
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 mt-6">
            <motion.button
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={cancel}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
            >
              Create Event
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
    </div>
  );
};

export default AddEventForm;